import {Grid, Typography} from "@mui/material";
import React, {useContext, useEffect, useMemo, useState} from "react";
import {useFormContext} from "react-hook-form";
import {useTranslation} from "react-i18next";
import {FormReportModel} from "../../../api/types/report";
import {FormAutocomplete, FormDatePicker} from "../../../common/form-fields";
import {useReportsRepository} from "../../../api/repositories/ReportRepository";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";
import {SelectElementModelBE, SelectElementModelFE} from "../../../api/types/generic";
import dayjs from "dayjs";

const CompetitorsBenchmarkForm = ({readOnly}: { readOnly: boolean }) => {
    // Use Translation
    const {t} = useTranslation(["translation", "pricing"]);

    //USE FORM CONTEXT
    const {register, setValue, watch} = useFormContext<FormReportModel>();

    // Use Context
    const {globalData: {user}} = useContext(GlobalContext);

    //USE STATE
    const [location, setLocation] = useState<string[]>([]);
    const [sites, setSites] = useState<string[]>([]);
    const [lor, setLor] = useState<string[]>([]);

    //USE HOOK CUSTOM
    const {getOffices, getSites} = useReportsRepository();

    // Tenant options from user
    const legalEntityOptions: SelectElementModelFE[] = useMemo(() => {
        return user.legalEntities?.map((el: SelectElementModelBE) => ({
            code: el.name,
            label: t('translation:legalEntities.'.concat(el.name))
        })) ?? [];
    }, [user.legalEntities, t]);

    // Watch tenant changes
    const selectedTenant = watch("competitorsBenchmark.tenant");

    const handleLocationsAndSites = async (tenant: string) => {
        try {
            const [locations, sitesData] = await Promise.all([
                getOffices(tenant),
                getSites(tenant)
            ]);
            setLocation(locations);
            setSites(sitesData);
        } catch (error) {
            console.error("Error loading locations and sites:", error);
        }
    };

    // Set default tenant if user has only one
    useEffect(() => {
        if (legalEntityOptions.length === 1 && !selectedTenant) {
            setValue("competitorsBenchmark.tenant", legalEntityOptions[0]);
        }
    }, [legalEntityOptions, selectedTenant, setValue]);

    // Rimuovo il useEffect per la data - il campo deve rimanere vuoto

    // Load locations and sites when tenant changes
    useEffect(() => {
        if (selectedTenant?.code) {
            handleLocationsAndSites(String(selectedTenant.code));
        }
    }, [selectedTenant]);

    useEffect(() => {
        const lorArray = Array.from(Array(365).keys());
        const lor = [];
        for (let i = 0; i < lorArray.length; i++) {
            lor.push((lorArray[i] + 1).toString());
        }
        setLor(lor);
        setValue("competitorsBenchmark.from", dayjs());
    }, []);

    const shouldShowTenant = legalEntityOptions.length > 1;

    return (
        <Grid container columnSpacing={2}>
            <Typography variant="h6" width={"100%"} pl={2} mt={4}>
                {t("pricing:report.labels.searchParameters")}
            </Typography>
            {/* TENANT */}
            {shouldShowTenant && (
                <Grid item xs={12} md={3}>
                    <FormAutocomplete
                        {...register("competitorsBenchmark.tenant")}
                        label={`${t("pricing:dealer.tenant")}*`}
                        options={legalEntityOptions}
                        isOptionEqualToValue={(option: SelectElementModelFE, value: SelectElementModelFE) =>
                            option?.code === value?.code
                        }
                        handleChange={(value: SelectElementModelFE) => {
                            setValue("competitorsBenchmark.tenant", value);
                            // Reset dependent fields when tenant changes
                            setValue("competitorsBenchmark.locationPickUp", null);
                            setValue("competitorsBenchmark.locationDropOff", null);
                            setValue("competitorsBenchmark.site", "");
                        }}
                        readOnly={readOnly}
                    />
                </Grid>
            )}
            {/* PICK UP FROM */}
            <Grid item xs={12} md={3}>
                <FormDatePicker
                    {...register("competitorsBenchmark.from")}
                    label={`${t("pricing:report.form.from")}*`}
                    minDate={dayjs()} // Disabilita le date passate
                    readOnly={readOnly}
                />
            </Grid>
            {/* LOCATION FROM */}
            <Grid item xs={12} md={3}>
                <FormAutocomplete
                    {...register("competitorsBenchmark.locationPickUp")}
                    label={`${t("pricing:report.form.locationPickUp")}*`}
                    options={location}
                    isOptionEqualToValue={(option: string, value: string) =>
                        option === value
                    }
                    handleChange={(value: string) => {
                        setValue("competitorsBenchmark.locationPickUp", value);
                        setValue("competitorsBenchmark.locationDropOff", value);
                    }}
                />
            </Grid>
            {/* LOCATION TO */}
            <Grid item xs={12} md={3}>
                <FormAutocomplete
                    {...register("competitorsBenchmark.locationDropOff")}
                    label={`${t("pricing:report.form.locationDropOff")}*`}
                    options={location}
                    isOptionEqualToValue={(option: string, value: string) =>
                        option === value
                    }
                />
            </Grid>
            {/* LENGTH OF RENTAL */}
            <Grid item xs={12} md={3}>
                <FormAutocomplete
                    {...register("competitorsBenchmark.rentalPeriod")}
                    label={`${t("pricing:report.form.rentalPeriod")}*`}
                    multiple={true}
                    options={lor}
                    isOptionEqualToValue={(option: string, value: string) =>
                        option === value
                    }
                />
            </Grid>
            {/* SITE */}
            <Grid item xs={12} md={3}>
                <FormAutocomplete
                    {...register("competitorsBenchmark.site")}
                    label={`${t("pricing:report.form.site")}*`}
                    options={sites}
                    isOptionEqualToValue={(option: string, value: string) =>
                        option === value
                    }
                />
            </Grid>
        </Grid>
    );
};

export default CompetitorsBenchmarkForm;
